import React, { useMemo } from 'react';
import styled from 'styled-components';

function findHighestNumberInList(numbers: number[]): number {
    return numbers.reduce<number>((highestNumber, number) => {
        if (number > highestNumber) {
            return number;
        }
        return highestNumber;
    }, 0);
}

const PortButtonList = styled.ul`
    font-weight: bold;
    margin: 0;
    padding: 0;
    list-style: none;
`;

const PortButtonItem = styled.li<{ height: number }>`
    padding-top: ${p => (p.height - 1) * 17 + 6}px;
    padding-bottom: ${p => (p.height - 1) * 17 + 6}px;
`;

interface PortTemplate {
    name: string;
    type: any;
}

interface DuplicatablePortListProps {
    groupName: string;
    groupTemplate: PortTemplate[];
    initialCopyCount?: number;
    nodeState: Record<string, any>;
    setNodeState: (key: string, value: any) => void;
    onPortsChange: (ports: PortTemplate[]) => void;
}

export const DuplicatablePortList: React.FC<DuplicatablePortListProps> = ({
    groupName,
    groupTemplate,
    initialCopyCount = 1,
    nodeState,
    setNodeState,
    onPortsChange,
}) => {
    const stateKey = 'duplicatedPortGroups' + groupName;
    const portGroupNumbers = nodeState[stateKey] || Array.from({ length: initialCopyCount }, (_, i) => i + 1);

    const setPortGroupNumbers = (numbers: number[]) => {
        setNodeState(stateKey, numbers);
    };

    const addElement = () => {
        const highestPortNumber = findHighestNumberInList(portGroupNumbers);
        const newNumbers = [...portGroupNumbers, highestPortNumber + 1];
        setPortGroupNumbers(newNumbers);
    };

    const removeElement = (portGroupNumber: number) => {
        const newNumbers = portGroupNumbers.filter((p: number) => p !== portGroupNumber);
        setPortGroupNumbers(newNumbers);
    };

    const ports: PortTemplate[] = useMemo(
        () =>
            portGroupNumbers.flatMap((portGroupNumber: number) =>
                groupTemplate.map(port => ({ ...port, name: port.name + ' ' + portGroupNumber })),
            ),
        [portGroupNumbers, groupTemplate],
    );

    // Notify parent of port changes
    React.useEffect(() => {
        onPortsChange(ports);
    }, [ports, onPortsChange]);

    return (
        <PortButtonList>
            {portGroupNumbers.map((portGroupNumber: number) => (
                <PortButtonItem height={groupTemplate.length} key={portGroupNumber} onClick={() => removeElement(portGroupNumber)}>
                    -
                </PortButtonItem>
            ))}
            <PortButtonItem height={1} onClick={addElement}>
                +
            </PortButtonItem>
        </PortButtonList>
    );
};

// Helper functions for port matching
export const doesPortMatchTemplate = (portName: string, template: PortTemplate): boolean => {
    return portName.startsWith(template.name);
};

export const getPortNameByTemplate = (portName: string, template: PortTemplate): string | undefined => {
    const portGroupNumberMatch = portName.match(/([0-9]+)$/);
    return portGroupNumberMatch ? `${template.name} ${portGroupNumberMatch[1]}` : undefined;
};
